
import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Users, UserPlus } from 'lucide-react';
import { InviteUserDialog } from '../InviteUserDialog';
import { useUserSearch } from './hooks/useUserSearch';
import { useUserManagement } from './hooks/useUserManagement';
import { SearchInput } from './SearchInput';
import { UserListContainer } from './UserListContainer';
import { EditUserDialog } from './EditUserDialog';
import { DeleteUserDialog } from './DeleteUserDialog';
import { ImpersonateDialog } from './ImpersonateDialog';
import { OptimizedInvitationsManagement } from './OptimizedInvitationsManagement';
import { useInvitations } from '@/hooks/useInvitations';

interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'superadmin' | 'member';
  created_at: string;
  is_verified: boolean;
  auth_user_id: string | null;
  created_from: 'self_registered' | 'invited';
}

export const UserManagementContainer = () => {
  const [editingUser, setEditingUser] = useState<Profile | null>(null);
  const [deletingUser, setDeletingUser] = useState<Profile | null>(null);
  const [impersonatingUser, setImpersonatingUser] = useState<Profile | null>(null);
  const [newRole, setNewRole] = useState<'superadmin' | 'member'>('member');
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('active');

  const { inputValue, setInputValue, searchTerm } = useUserSearch();
  const { updateRoleMutation, deleteUserMutation, handleImpersonation } = useUserManagement();
  const { data: invitations } = useInvitations();

  const handleEditUser = useCallback((user: Profile) => {
    setEditingUser(user);
    setNewRole(user.role);
  }, []);

  const handleUpdateRole = useCallback(() => {
    if (!editingUser || !newRole) return;
    updateRoleMutation.mutate(
      { userId: editingUser.id, role: newRole },
      {
        onSuccess: () => {
          setEditingUser(null);
        }
      }
    );
  }, [editingUser, newRole, updateRoleMutation]);

  const handleDeleteUser = useCallback((user: Profile) => {
    setDeletingUser(user);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deletingUser) return;
    deleteUserMutation.mutate(deletingUser.id, {
      onSuccess: () => {
        setDeletingUser(null);
      }
    });
  }, [deletingUser, deleteUserMutation]);

  const handleImpersonateUser = useCallback((user: Profile) => {
    setImpersonatingUser(user);
  }, []);

  const handleConfirmImpersonate = useCallback(async () => {
    if (!impersonatingUser) return;
    
    setImpersonatingUserId(impersonatingUser.id);
    
    try {
      await handleImpersonation(
        impersonatingUser.id,
        `${impersonatingUser.first_name} ${impersonatingUser.last_name}`
      );
    } finally {
      setImpersonatingUserId(null);
      setImpersonatingUser(null);
    }
  }, [impersonatingUser, handleImpersonation]);


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">User Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage all users and invitations across the entire system
          </p>
        </div>
        <InviteUserDialog />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Active Members
            
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Pending Invitations
            {invitations && <Badge variant="secondary" className="ml-1">{invitations.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-4">
          {/* Search */}
          <SearchInput
            value={inputValue}
            onChange={setInputValue}
            placeholder="Search members..."
          />

          {/* Members Table */}
          <UserListContainer
            searchTerm={searchTerm}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onImpersonateUser={handleImpersonateUser}
            impersonatingUserId={impersonatingUserId}
          />
        </TabsContent>

        <TabsContent value="invitations">
          <OptimizedInvitationsManagement />
        </TabsContent>
      </Tabs>

      {/* Edit User Dialog */}
      <EditUserDialog
        user={editingUser}
        isOpen={!!editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
        newRole={newRole}
        onRoleChange={setNewRole}
        onUpdate={handleUpdateRole}
        isUpdating={updateRoleMutation.isPending}
      />

      {/* Delete User Dialog */}
      <DeleteUserDialog
        user={deletingUser}
        isOpen={!!deletingUser}
        onOpenChange={(open) => !open && setDeletingUser(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteUserMutation.isPending}
      />

      {/* Impersonation Dialog */}
      <ImpersonateDialog
        user={impersonatingUser}
        isOpen={!!impersonatingUser}
        onOpenChange={(open) => !open && setImpersonatingUser(null)}
        onConfirm={handleConfirmImpersonate}
        isImpersonating={!!impersonatingUserId}
      />
    </div>
  );
};
